import React from 'react';
import {
  CheckCircle,
  Circle,
  CircleNotch,
  ClipboardText,
  Eye,
  ListChecks,
  Question,
  XCircle,
} from '@phosphor-icons/react';
import { Flex, Text, TruncatedText } from '@chatsdet/ui';
import type {
  ExploratoryTestLiveChecklistItem,
  ExploratoryTestObservation,
  ExploratoryTestObservationKind,
} from '../../../features/exploratory-tests/types';
import { type PlanItem, type PlanPriority, type PlanStatus } from '../ExploratoryTestVerdict';
import { PriorityChip } from './PlanPanel';
import type { ExploratoryTestData } from './types';

const planStatusMeta: Record<
  PlanStatus,
  { label: string; icon: React.ElementType; iconClass: string; labelClass?: string }
> = {
  failed: {
    label: 'Failed',
    icon: XCircle,
    iconClass: 'text-red-500',
    labelClass: 'text-destructive',
  },
  passed: { label: 'Passed', icon: CheckCircle, iconClass: 'text-emerald-500' },
  skipped: { label: 'Skipped', icon: Circle, iconClass: 'text-muted-foreground/50' },
  question: { label: 'Question', icon: Question, iconClass: 'text-blue-500' },
};

/** @internal exported for unit tests; the Report tab is the only production caller. */
export function ReportPanel({ test }: { test: ExploratoryTestData }) {
  const testPlan = (test.reportJson?.testPlan ?? []) as PlanItem[];
  const appended = (test.reportJson?.appended ?? []) as PlanItem[];
  const observations = test.reportJson?.observations ?? [];
  const hasFinalReport = !!test.summary || testPlan.length > 0 || appended.length > 0;

  const liveItems = test.liveTestPlan ?? [];
  const isInFlight =
    test.status === 'pending' || test.status === 'planning' || test.status === 'running';
  if (!hasFinalReport && isInFlight && liveItems.length > 0) {
    return <LiveReportPanel items={liveItems} />;
  }

  if (!hasFinalReport) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground px-4 text-center">
        <Text size="sm">No report yet. The agent writes a final report when it finishes.</Text>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto py-3 space-y-4">
      {test.summary && (
        <div className="px-3">
          <Flex align="center" gap="sm" className="mb-1.5">
            <ClipboardText size={14} weight="duotone" className="text-muted-foreground" />
            <Text
              weight="medium"
              size="xs"
              className="uppercase tracking-wide text-muted-foreground"
            >
              Summary
            </Text>
          </Flex>
          <Text size="sm" className="text-foreground leading-relaxed whitespace-pre-wrap">
            {test.summary}
          </Text>
        </div>
      )}

      <ObservationsSection items={observations} />

      <TestPlanList items={testPlan} />

      {appended.length > 0 && (
        <div>
          <Flex align="center" gap="sm" className="mb-1.5 px-3">
            <ListChecks size={14} weight="duotone" className="text-muted-foreground" />
            <Text
              weight="medium"
              size="xs"
              className="uppercase tracking-wide text-muted-foreground"
            >
              Discovered (not in plan)
            </Text>
          </Flex>
          <TestPlanList items={appended} />
        </div>
      )}
    </div>
  );
}

function LiveReportPanel({ items }: { items: ReadonlyArray<ExploratoryTestLiveChecklistItem> }) {
  const done = items.filter(
    (item) => item.status !== 'pending' && item.status !== 'in_progress'
  ).length;
  return (
    <div className="h-full overflow-y-auto py-3 space-y-3">
      <Flex align="center" gap="sm" className="px-3">
        <CircleNotch size={14} weight="bold" className="text-blue-500 animate-spin" />
        <Text weight="medium" size="xs" className="uppercase tracking-wide text-muted-foreground">
          Running checklist
        </Text>
        <Text size="xs" className="text-muted-foreground ml-auto">
          {done}/{items.length} complete
        </Text>
      </Flex>
      <LiveTestPlanList items={items} />
    </div>
  );
}

function LiveStatusIcon({ status }: { status: ExploratoryTestLiveChecklistItem['status'] }) {
  if (status === 'pending') {
    return (
      <Circle size={14} weight="regular" className="text-muted-foreground/40 shrink-0 mt-0.5" />
    );
  }
  if (status === 'in_progress') {
    return (
      <CircleNotch size={14} weight="bold" className="text-blue-500 animate-spin shrink-0 mt-0.5" />
    );
  }
  const meta = planStatusMeta[status];
  const Icon = meta.icon;
  return <Icon size={14} weight="fill" className={`${meta.iconClass} shrink-0 mt-0.5`} />;
}

function PlanItemRow({
  icon,
  name,
  priority,
  details,
}: {
  icon: React.ReactNode;
  name: string;
  priority: PlanPriority | null;
  details: string | null;
}) {
  return (
    <div className="px-3 py-2">
      <Flex align="start" gap="sm">
        {icon}
        <div className="min-w-0 flex-1">
          <Flex align="center" gap="xs" className="flex-wrap">
            {priority && <PriorityChip priority={priority} />}
            <TruncatedText text={name} lines={2} className="text-xs text-foreground" />
          </Flex>
          {details && (
            <Text size="xs" className="text-muted-foreground mt-0.5 leading-relaxed">
              {details}
            </Text>
          )}
        </div>
      </Flex>
    </div>
  );
}

/** @internal exported for unit tests; LiveReportPanel is the only production caller. */
export function LiveTestPlanList({
  items,
}: {
  items: ReadonlyArray<ExploratoryTestLiveChecklistItem>;
}) {
  if (items.length === 0) return null;
  return (
    <div className="divide-y divide-border/60 border-y border-border/60">
      {items.map((item) => (
        <PlanItemRow
          key={item.id}
          icon={<LiveStatusIcon status={item.status} />}
          name={item.name}
          priority={item.priority ?? null}
          details={item.details ?? null}
        />
      ))}
    </div>
  );
}

const PRIORITY_SORT_ORDER: Record<PlanPriority, number> = { p0: 0, p1: 1, p2: 2, p3: 3 };
const STATUS_SORT_ORDER: Record<PlanStatus, number> = {
  failed: 0,
  question: 1,
  skipped: 2,
  passed: 3,
};

function sortPlanItems(items: ReadonlyArray<PlanItem>): PlanItem[] {
  return [...items].sort((a, b) => {
    const pa = PRIORITY_SORT_ORDER[a.priority ?? 'p1'];
    const pb = PRIORITY_SORT_ORDER[b.priority ?? 'p1'];
    if (pa !== pb) return pa - pb;
    return STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
  });
}

const OBSERVATION_KIND_LABEL: Record<ExploratoryTestObservationKind, string> = {
  ux_oddity: 'UX oddity',
  confusion: 'Confusion',
  rough_edge: 'Rough edge',
};

/**
 * UX oddities, confusions, and rough edges captured by the tester.
 *
 * @internal exported for unit tests; ReportPanel is the only production caller.
 */
export function ObservationsSection({
  items,
}: {
  items: ReadonlyArray<ExploratoryTestObservation>;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <Flex align="center" gap="sm" className="mb-1.5 px-3">
        <Eye size={14} weight="duotone" className="text-muted-foreground" />
        <Text weight="medium" size="xs" className="uppercase tracking-wide text-muted-foreground">
          Observations ({items.length})
        </Text>
      </Flex>
      <div className="divide-y divide-border/60 border-y border-border/60">
        {items.map((obs, i) => (
          <div key={i} className="px-3 py-2">
            <Flex align="center" gap="xs" className="flex-wrap">
              <span className="rounded bg-muted px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-muted-foreground shrink-0">
                {OBSERVATION_KIND_LABEL[obs.kind] ?? 'Note'}
              </span>
              <TruncatedText
                text={obs.title}
                lines={2}
                className="text-xs text-foreground font-medium"
              />
            </Flex>
            <Text size="xs" className="text-muted-foreground mt-0.5 leading-relaxed">
              {obs.note}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

/** @internal exported for unit tests; ReportPanel is the only production caller. */
export function TestPlanList({ items }: { items: ReadonlyArray<PlanItem> }) {
  const sorted = sortPlanItems(items);
  if (sorted.length === 0) return null;
  return (
    <div className="divide-y divide-border/60 border-y border-border/60">
      {sorted.map((item, i) => {
        const meta = planStatusMeta[item.status];
        const Icon = meta.icon;
        return (
          <PlanItemRow
            key={i}
            icon={<Icon size={14} weight="fill" className={`${meta.iconClass} shrink-0 mt-0.5`} />}
            name={item.name}
            priority={item.priority ?? null}
            details={item.details ?? null}
          />
        );
      })}
    </div>
  );
}
